import { gql } from '@apollo/client';

// ─── Reservation Mutations ──────────────────────────────────────────────────

export const CREATE_WORKSTATION_RESERVATION = gql`
  mutation (
    $workstationId: String!
    $tableId: String!
    $floorId: Int!
    $areaId: Int!
    $userEmail: String!
    $date: DateTime!
    $fullName: String!
    $phoneNumber: String!
    $traveledTo: String!
    $tripReturnDate: String!
    $status: Boolean!
    $statusReservation: Int!
    $needsParking: Boolean!
    $lunch: Boolean!
    $lunchTime: String!
    $lunchVegan: Boolean!
    $lunchGlutenFree: Boolean!
    $bookedVia: String
  ) {
    reservation: createWorkstationReservation(
      workstationReservation: {
        workstationId: $workstationId
        tableId: $tableId
        floorId: $floorId
        areaId: $areaId
        status: $status
      }
      reservation: {
        userEmail: $userEmail
        date: $date
        fullName: $fullName
        phoneNumber: $phoneNumber
        traveledTo: $traveledTo
        tripReturnDate: $tripReturnDate
        status: $statusReservation
        needsParking: $needsParking
        lunch: $lunch
        lunchTime: $lunchTime
        lunchVegan: $lunchVegan
        lunchGlutenFree: $lunchGlutenFree
        bookedVia: $bookedVia
      }
    ) {
      reservationId
    }
  }
`;

export const ADMIN_CREATE_WORKSTATION_RESERVATION = gql`
  mutation (
    $workstationId: String!
    $tableId: String!
    $floorId: Int!
    $areaId: Int!
    $date: DateTime!
    $fullName: String!
    $userEmail: String
    $lunch: Boolean
    $lunchTime: String
    $lunchVegan: Boolean
    $lunchGlutenFree: Boolean
  ) {
    reservation: adminCreateWorkstationReservation(
      input: {
        workstationId: $workstationId
        tableId: $tableId
        floorId: $floorId
        areaId: $areaId
        date: $date
        fullName: $fullName
        userEmail: $userEmail
        lunch: $lunch
        lunchTime: $lunchTime
        lunchVegan: $lunchVegan
        lunchGlutenFree: $lunchGlutenFree
      }
    ) {
      reservationId
      status
    }
  }
`;

export const DELETE_WORKSTATION_RESERVATION = gql`
  mutation ($reservationID: Float!) {
    result: deleteWorkstationReservation(reservationID: $reservationID)
  }
`;

export const CHECK_IN_RESERVATION = gql`
  mutation ($id: Float!) {
    result: checkInReservation(id: $id)
  }
`;

export const CHECK_OUT_RESERVATION = gql`
  mutation ($id: Float!) {
    result: checkOutReservation(id: $id)
  }
`;

export const DISABLE_WORKSTATION_RESERVATION = gql`
  mutation ($id: Float!) {
    result: disableWorkstationReservation(id: $id)
  }
`;

export const CHANGE_WORKSTATION = gql`
  mutation (
    $reservationId: Int!
    $newWorkstationId: String!
    $newTableId: String!
    $newFloorId: Int!
    $newAreaId: Int!
  ) {
    changeWorkstation(
      input: {
        reservationId: $reservationId
        newWorkstationId: $newWorkstationId
        newTableId: $newTableId
        newFloorId: $newFloorId
        newAreaId: $newAreaId
      }
    ) {
      reservationId
      workstationId
      tableId
      floorId
      areaId
    }
  }
`;

// ─── Waitlist Mutations ─────────────────────────────────────────────────────

export const EDIT_LUNCH = gql`
  mutation (
    $reservationId: Int!
    $lunch: Boolean!
    $lunchTime: String
    $lunchVegan: Boolean
    $lunchGlutenFree: Boolean
  ) {
    editLunch(
      input: {
        reservationId: $reservationId
        lunch: $lunch
        lunchTime: $lunchTime
        lunchVegan: $lunchVegan
        lunchGlutenFree: $lunchGlutenFree
      }
    ) {
      id
      lunch
      lunchTime
      lunchVegan
      lunchGlutenFree
    }
  }
`;

export const SUBMIT_LUNCH_SURVEY = gql`
  mutation (
    $reservationId: Int!
    $email: String!
    $rating: Int!
    $comment: String
  ) {
    submitLunchSurvey(
      input: {
        reservationId: $reservationId
        email: $email
        rating: $rating
        comment: $comment
      }
    ) {
      id
      lunchSurveyRating
      lunchSurveyComment
    }
  }
`;

export const JOIN_WAITLIST = gql`
  mutation ($input: JoinWaitlistInput!) {
    joinWaitlist(input: $input) {
      id
      userEmail
      floorId
      date
    }
  }
`;

export const LEAVE_WAITLIST = gql`
  mutation ($userEmail: String!, $floorId: Int!, $date: String!) {
    leaveWaitlist(userEmail: $userEmail, floorId: $floorId, date: $date)
  }
`;

// ─── Admin Lunch Mutations ──────────────────────────────────────────────────

export const TOGGLE_ADMIN_LUNCH_ORDER = gql`
  mutation ToggleAdminLunchOrder($input: ToggleAdminLunchOrderInput!) {
    toggleAdminLunchOrder(input: $input) {
      id
      adminLunchPersonId
      date
      lunchTime
      lunchChecked
      lunchVegan
      lunchGlutenFree
    }
  }
`;

export const CANCEL_ADMIN_LUNCH_ORDER = gql`
  mutation CancelAdminLunchOrder($id: Int!) {
    cancelAdminLunchOrder(id: $id) {
      id
      lunchCancelledAt
      lunchCancelledBy
    }
  }
`;

export const RESTORE_ADMIN_LUNCH_ORDER = gql`
  mutation RestoreAdminLunchOrder($id: Int!) {
    restoreAdminLunchOrder(id: $id) {
      id
      lunchCancelledAt
      lunchCancelledBy
    }
  }
`;

export const CREATE_ADMIN_LUNCH_PERSON = gql`
  mutation CreateAdminLunchPerson($input: CreateAdminLunchPersonInput!) {
    createAdminLunchPerson(input: $input) {
      id
      fullName
      email
      area
      isActive
      requiresQr
      alwaysSelected
    }
  }
`;

export const UPDATE_ADMIN_LUNCH_PERSON = gql`
  mutation UpdateAdminLunchPerson(
    $id: Int!
    $input: UpdateAdminLunchPersonInput!
  ) {
    updateAdminLunchPerson(id: $id, input: $input) {
      id
      fullName
      email
      area
      isActive
      requiresQr
      alwaysSelected
      lunchVegan
    }
  }
`;

export const DELETE_ADMIN_LUNCH_PERSON = gql`
  mutation DeleteAdminLunchPerson($id: Int!) {
    deleteAdminLunchPerson(id: $id)
  }
`;

// ─── Extra Lunch Mutations ──────────────────────────────────────────────────

export const CREATE_EXTRA_LUNCH_ASSIGNMENT = gql`
  mutation CreateExtraLunchAssignment($input: CreateExtraLunchAssignmentInput!) {
    createExtraLunchAssignment(input: $input) {
      id
      date
      recipientName
      recipientEmail
      recipientArea
      lunchTime
      lunchVegan
      lunchGlutenFree
      notes
    }
  }
`;

export const DELETE_EXTRA_LUNCH_ASSIGNMENT = gql`
  mutation DeleteExtraLunchAssignment($id: Int!) {
    deleteExtraLunchAssignment(id: $id)
  }
`;

export const REASSIGN_LUNCH = gql`
  mutation ReassignLunch($input: ReassignLunchInput!) {
    reassignLunch(input: $input) {
      id
      date
      recipientName
      recipientEmail
      lunchTime
      sourceReservationId
    }
  }
`;

export const REASSIGN_ADMIN_LUNCH = gql`
  mutation ReassignAdminLunch($input: ReassignAdminLunchInput!) {
    reassignAdminLunch(input: $input) {
      id
      date
      recipientName
      recipientEmail
      lunchTime
      sourceAdminLunchOrderId
    }
  }
`;

export const RESEND_EXTRA_LUNCH_EMAIL = gql`
  mutation ResendExtraLunchEmail($id: Int!) {
    resendExtraLunchEmail(id: $id)
  }
`;

export const TOGGLE_EXTRA_LUNCH_CHECKED = gql`
  mutation ToggleExtraLunchChecked($id: Int!) {
    toggleExtraLunchChecked(id: $id) {
      id
      lunchChecked
      lunchCheckedAt
      lunchCheckedVia
    }
  }
`;

export const SEND_LATE_CANCELLATIONS_REPORT = gql`
  mutation SendLateCancellationsReport($date: String!) {
    sendLateCancellationsReport(date: $date)
  }
`;

// ─── Event Mutations ─────────────────────────────────────────────────────────

export const CREATE_EVENT = gql`
  mutation CreateEvent($input: CreateEventInput!) {
    createEvent(input: $input) {
      id
      date
      title
      description
      type
    }
  }
`;

export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: Int!) {
    deleteEvent(id: $id)
  }
`;

// ─── User Preferences ────────────────────────────────────────────────────────

/**
 * Saves the caller's preferred UI language ('es' | 'en') on their own account.
 * Scoped server-side to the authenticated email.
 */
export const SET_MY_LANGUAGE = gql`
  mutation SetMyLanguage($language: String!) {
    setMyLanguage(language: $language) {
      email
      language
    }
  }
`;

export const REACTIVATE_NO_SHOW_LUNCH = gql`
  mutation ReactivateNoShowLunch($reservationId: Float!) {
    reactivateNoShowLunch(reservationId: $reservationId)
  }
`;

export const REPORT_WORKSTATION_ISSUE = gql`
  mutation ($input: ReportWorkstationIssueInput!) {
    issue: reportWorkstationIssue(input: $input) {
      id
    }
  }
`;
